import React, { useEffect, useState } from 'react';
import { Modal } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { postcodeToPostalState } from 'aus-postcode';
import { SLICES } from '../../../../../constants/slices';
import { AddressManualEntryPanel } from '../addressManualEntry/addressManualEntryPanel';
import { AddressSearch } from '../addressSearch/addressSearch';
import { PolicyHolderPropTypes } from '../../../../../propTypes/petDetails';
import './addressDetailsDialog.scss';
import { useGetFormatAddressQuery, useUpdateAddressMutation } from '../../../../../data/customer';
import { useGetPetInfoQuery } from '../../../../../data/policy';
import { FORM_KEYS } from '../../../../../constants/formKeys';
import { VALIDATION_ERROR_MESSAGE } from '../../../../../constants/errorMessage';

const { POLICY_HOLDER_ADDRESS_AUTOCOMPLETE_SEARCH } = FORM_KEYS;

export const AddressDetailsDialog = ({ policyHolder, onClose = () => {}, setShowSuccessMessage }) => {
  const fieldFormContext = useFormContext();
  const { reset, watch } = fieldFormContext;
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { auth0AccessToken } = authDetails;
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [newAddress, setNewAddress] = useState(null);
  const [globalAddressKey, setGlobalAddressKey] = useState(null);
  const { data: petDetails } = useGetPetInfoQuery(auth0AccessToken, {
    skip: !auth0AccessToken
  });
  const { data: selectedAddressFormat } = useGetFormatAddressQuery(globalAddressKey, {
    skip: globalAddressKey === null
  });
  const [updateAddress, { isSuccess }] = useUpdateAddressMutation();

  const { street, suburb, postcode } = policyHolder;

  const state = postcodeToPostalState(postcode);

  const handleAddressUpdate = () => {
    const updatedAddress = {
      barCode: selectedAddressFormat?.responseObject?.Barcode,
      dpid: selectedAddressFormat?.responseObject?.Dpid,
      partnerNumber: petDetails[0]?.PartnerNumber,
      policyNumber: null,
      postcode: selectedAddressFormat?.responseObject?.Postcode,
      state: selectedAddressFormat?.responseObject?.Region,
      street: selectedAddressFormat?.responseObject?.AddressLine,
      suburb: selectedAddressFormat?.responseObject?.Locality
    };

    // When search is empty
    if (!updatedAddress.street || !updatedAddress.suburb || !updatedAddress.postcode || !updatedAddress.state) {
      fieldFormContext.setError(POLICY_HOLDER_ADDRESS_AUTOCOMPLETE_SEARCH, {
        type: 'manual',
        message: VALIDATION_ERROR_MESSAGE.INVALID_ADDRESS
      });
      return;
    }

    // When user clears the search and tries to update
    if (!newAddress) {
      fieldFormContext.setError(POLICY_HOLDER_ADDRESS_AUTOCOMPLETE_SEARCH, {
        type: 'manual',
        message: VALIDATION_ERROR_MESSAGE.INVALID_ADDRESS
      });
      return;
    }
    updateAddress(updatedAddress);
  };

  const HandleAddressSelect = (data) => {
    setNewAddress(data.mainText);
    setGlobalAddressKey(data.globalAddressKey);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (isSuccess) {
      reset();
      onClose();
      setShowSuccessMessage(isSuccess);
    }
  }, [isSuccess, onClose, setShowSuccessMessage, reset]);

  const renderFooter = (
    <div className="footerModalContainer">
      <button
        type="button"
        className="button primary outline"
        onClick={handleClose}
      >
        <span className="labelText">Cancel</span>
      </button>

      <button
        type="button"
        className="button primary"
        onClick={handleAddressUpdate}
      >
        <span className="labelText">Update</span>
      </button>
    </div>
  );

  return (
    <Modal
      size="small"
      open
      onClose={handleClose}
      header={<h2 className="modal-title">Update address details</h2>}
      footer={renderFooter}
      showFooterDivider
    >
      {isManualEntry ? (
        <>
          <AddressManualEntryPanel fieldFormContext={fieldFormContext} />
          <p>
            Prefer to search for your address?
            <button
              type="button"
              className="button primary text_only"
              onClick={() => setIsManualEntry(false)}
            >
              <span className="labelText">Use address search</span>
            </button>
          </p>
        </>
      ) : (
        <>
          <AddressSearch
            onSelect={HandleAddressSelect}
            fieldFormContext={fieldFormContext}
            setNewAddress={setNewAddress}
          />
          {/* Hiding this for now since this does not yet exist in CSP 2.5
           <p>
            Prefer to enter your address manually?
            <button
              type="button"
              className="button primary text_only"
              onClick={() => setIsManualEntry(true)}
            >
              <span className="labelText">Manually enter address</span>
            </button>
          </p> */}
        </>
      )}

      {newAddress && (
        <section className="addressSummary">
          <span className="labelValueText">
            <em>New address:</em>
            <strong>{newAddress}</strong>
          </span>
        </section>
      )}

      <section className="addressSummary">
        <span className="labelValueText">
          <em>Current address:</em>
          <strong>
            {street}, {suburb}, {postcode}, {state.abbreviation}
          </strong>
        </span>
      </section>
    </Modal>
  );
};

AddressDetailsDialog.propTypes = {
  policyHolder: PolicyHolderPropTypes,
  onClose: PropTypes.func,
  setShowSuccessMessage: PropTypes.func
};
